/**
 * Period Closure Wizard — SB-driven 5-step Periodenabschluss panel.
 *
 * Hybrid mode:
 * - Step tiles at the top are clickable in any order (free mode)
 * - "Weiter / Zurück / Überspringen / Speichern & schließen" for guided mode
 * - Wizard state persists server-side (period_closures collection)
 *
 * Steps:
 *   1. precheck      — automatic findings list with quick-fix links
 *   2. bookings      — inline-editable booking list with required reason
 *   3. attachments   — Tresor document picker
 *   4. report        — generate PDF, preview KPIs
 *   5. finalize      — lock period + email FL
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, Check, AlertTriangle, AlertCircle, Info,
  FileSearch, ListChecks, Paperclip, FileText, Lock, MessageSquare,
  Loader2, Send, ExternalLink, Filter, Download, Archive, Edit3,
  ReceiptText, ArrowUp, ArrowDown, ArrowRight, Ban, Trash2, Undo2,
} from 'lucide-react';
import { downloadBlob, extractBlobError } from '@/utils/downloadBlob';
import { onAdvisorChange } from '@/utils/advisorBus';
import HelpOverlay, { useHelpOverlay, HelpButton } from '@/components/HelpOverlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AdvisorInvestmentAllowanceControl from '@/components/advisor/AdvisorInvestmentAllowanceControl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem('advisor_token')}` });

const STEPS = [
  { id: 'precheck',    label: 'Pre-Check',    icon: FileSearch,    description: 'Automatische Plausibilitäts-Checks' },
  { id: 'bookings',    label: 'Buchungen',    icon: ListChecks,    description: 'Übersicht: geprüft / offen / Hinweise' },
  { id: 'attachments', label: 'Anlagen',      icon: Paperclip,     description: 'Kontoauszüge, USt-Nachweise' },
  { id: 'report',      label: 'Bericht',      icon: FileText,      description: 'PDF · DATEV · ZIP-Bundle' },
  { id: 'finalize',    label: 'Festschreiben',icon: Lock,          description: 'Lock + E-Mail an FL' },
];

const SEVERITY_CSS = {
  error: 'bg-red-50 border-red-200 text-red-800',
  warn:  'bg-amber-50 border-amber-200 text-amber-800',
  info:  'bg-slate-50 border-slate-200 text-slate-700',
};
const SEVERITY_ICON = { error: AlertTriangle, warn: AlertCircle, info: Info };

const fmtEur = (n) => new Intl.NumberFormat('de-DE',
  { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }
).format(Number(n) || 0);

export default function PeriodClosureWizard({ open, onClose, clientId, periodReview, onFinalized, canEdit = false }) {
  const [closure, setClosure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('precheck');

  // Open or create the closure on mount
  useEffect(() => {
    if (!open || !periodReview) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.post(`${API}/advisor/closures`, {
          client_id: clientId, period_review_id: periodReview.id,
        }, { headers: H() });
        if (!alive) return;
        setClosure(r.data);
        setStep(r.data.current_step || 'precheck');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Konnte Abschluss nicht öffnen');
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, periodReview, clientId, onClose]);

  const refresh = useCallback(async () => {
    if (!closure) return;
    const r = await axios.get(`${API}/advisor/closures/${closure.id}`, { headers: H() });
    setClosure(r.data);
  }, [closure]);

  // Cross-Tab-Auto-Refresh (Juni 2026): Buchungen werden in neuem Tab geöffnet.
  // Speichert der SB dort eine Änderung, läuft der Pre-Check hier automatisch neu
  // und die Buchungsliste lädt neu (StepBookings hört auf das closure-Objekt).
  useEffect(() => {
    if (!closure) return;
    return onAdvisorChange(closure.client_id, async () => {
      try {
        await axios.post(`${API}/advisor/closures/${closure.id}/precheck`, {}, { headers: H() });
      } catch { /* ignore */ }
      await refresh();
      toast.message('Nach Änderung aktualisiert');
    });
  }, [closure, refresh]);

  const goStep = useCallback(async (target) => {
    if (!closure) return;
    setStep(target);
    try {
      await axios.put(`${API}/advisor/closures/${closure.id}`,
        { current_step: target }, { headers: H() });
    } catch {}
  }, [closure]);

  const markStepDone = useCallback(async (id) => {
    if (!closure) return;
    const completed = Array.from(new Set([...(closure.completed_steps || []), id]));
    await axios.put(`${API}/advisor/closures/${closure.id}`,
      { completed_steps: completed }, { headers: H() });
    setClosure(c => ({ ...c, completed_steps: completed }));
  }, [closure]);

  // P3 z-index-Fix (Feb 2026): Wenn der Wizard offen ist, dimmen sich
  // Feedback-Buttons via window-Event statt CSS-Race.
  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent('ivoliq:wizard-open'));
    return () => window.dispatchEvent(new CustomEvent('ivoliq:wizard-close'));
  }, [open]);

  // Step-Action-Logic (Iter 71 Feedback): „Weiter" ist nun pro Step kontextspezifisch
  // benannt und führt die Schritt-Aktion aus (z.B. PDF erzeugen, festschreiben).
  // Step-Action-Buttons in den Bodies wurden entfernt — der Footer ist die einzige
  // Action-Quelle, das macht den Workflow eindeutig.
  const [actionRunning, setActionRunning] = useState(false);

  // Step 3 (Attachments): selected-State hier oben, damit die Footer-Action
  // das Speichern ausführen kann (Iter 72 Feedback — Inline-„Anlagen speichern"
  // war redundant zum Footer-„Anlagen abschließen").
  const [attachmentSelected, setAttachmentSelected] = useState(new Set());

  // Help-Overlay (Iter 72): `?` öffnet Tastatur-Shortcut-Hilfe für SB-Onboarding.
  // Nur aktiv wenn Wizard offen.
  const help = useHelpOverlay({ enabled: open });
  const stepActions = useMemo(() => ({
    precheck:    { label: 'Pre-Check abschließen' },
    bookings:    { label: 'Buchungen abschließen' },
    attachments: {
      label: 'Anlagen speichern & abschließen',
      handler: async () => {
        if (!closure) return;
        // Speichert ausgewählte Anlagen; bei Fehler wird die Aktion abgebrochen.
        await axios.post(`${API}/advisor/closures/${closure.id}/attachments`,
          { doc_ids: Array.from(attachmentSelected) }, { headers: H() });
        toast.success('Anlagen gespeichert');
        await refresh();
      },
    },
    report: {
      label: closure?.report_pdf_doc_id ? 'Weiter zum Festschreiben' : 'PDF-Bericht erzeugen & weiter',
      handler: async () => {
        if (!closure?.report_pdf_doc_id) {
          const r = await axios.post(`${API}/advisor/closures/${closure.id}/generate-report`,
            {}, { headers: H() });
          toast.success(`Bericht erzeugt — ${r.data.filename}`);
          await refresh();
        }
      },
    },
    finalize: {
      label: 'Festschreiben & senden',
      isFinal: true,
      confirm: 'Abschluss jetzt festschreiben?\n\nDie Periode wird GoBD-konform gesperrt und der Bericht per E-Mail an den Mandanten versendet. Spätere Änderungen sind nur mit dokumentiertem Aufbrechen möglich.',
      handler: async () => {
        const r = await axios.post(`${API}/advisor/closures/${closure.id}/finalize`,
          {}, { headers: H() });
        toast.success(r.data.email_sent
          ? 'Abschluss festgeschrieben & E-Mail versandt'
          : 'Abschluss festgeschrieben (E-Mail-Versand fehlgeschlagen — bitte später erneut prüfen)');
        await refresh();
        if (onFinalized) onFinalized();
        if (onClose) onClose();
      },
      disabledIf: () => !closure?.report_pdf_doc_id,
    },
  // eslint-disable-next-line
  }), [closure, refresh, onFinalized, onClose, attachmentSelected]);

  const handleStepAction = async () => {
    const cfg = stepActions[step];
    if (!cfg) return;
    if (cfg.confirm && !window.confirm(cfg.confirm)) return;
    setActionRunning(true);
    try {
      if (cfg.handler) await cfg.handler();
      await markStepDone(step);
      if (!cfg.isFinal) {
        const idx = STEPS.findIndex(s => s.id === step);
        if (idx < STEPS.length - 1) goStep(STEPS[idx + 1].id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Aktion');
    } finally {
      setActionRunning(false);
    }
  };

  // Tastatur-Shortcuts: 1–5 → Step direkt ansteuern; ←/→ Prev/Next
  // (Hotkey-Set bewusst klein gehalten, um nicht mit Browser-Defaults zu kollidieren)
  useEffect(() => {
    if (!open || loading || !closure) return;
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = STEPS.findIndex(s => s.id === step);
      if (e.key >= '1' && e.key <= '5') {
        const target = STEPS[parseInt(e.key, 10) - 1];
        if (target) { e.preventDefault(); goStep(target.id); }
      } else if (e.key === 'ArrowLeft' && idx > 0) {
        e.preventDefault();
        goStep(STEPS[idx - 1].id);
      } else if (e.key === 'ArrowRight' && idx < STEPS.length - 1) {
        e.preventDefault();
        goStep(STEPS[idx + 1].id);
      } else if (e.key === 'Escape' && !help.isOpen) {
        // Esc: erst Overlay zu (handled von Hook), sonst Wizard schließen
        onClose && onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, closure, step, help.isOpen, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="closure-wizard">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-4xl bg-white shadow-2xl border-l border-zinc-200 flex flex-col">
        {/* Header */}
        <div className="border-b border-zinc-200 px-5 py-3 flex items-center justify-between bg-zinc-50">
          <div>
            <div className="text-xs text-slate-500">Periodenabschluss</div>
            <div className="text-base font-semibold text-slate-900">{periodLabel(periodReview)}</div>
          </div>
          <div className="flex items-center gap-2">
            <HelpButton onClick={help.toggle} testId="wizard-help-btn" />
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-wizard-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading || !closure ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Step tiles — clickable in any order */}
            <div className="grid grid-cols-5 gap-1 p-3 border-b border-zinc-200 bg-white">
              {STEPS.map((s, idx) => {
                const Icon = s.icon;
                const isActive = step === s.id;
                const isCompleted = (closure.completed_steps || []).includes(s.id);
                const isSkipped = (closure.skipped_steps || []).includes(s.id);
                return (
                  <button key={s.id} onClick={() => goStep(s.id)}
                    className={`p-2.5 rounded-lg border text-left transition-colors ${
                      isActive ? 'border-emerald-600 bg-emerald-50/50 ring-1 ring-emerald-200'
                      : isCompleted ? 'border-emerald-200 bg-emerald-50/30 hover:bg-emerald-50'
                      : isSkipped ? 'border-zinc-200 bg-zinc-50 opacity-60 hover:opacity-100'
                      : 'border-zinc-200 bg-white hover:bg-zinc-50'
                    }`}
                    data-testid={`step-tile-${s.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <Icon className="w-3.5 h-3.5 text-slate-700" strokeWidth={1.7} />
                      {isCompleted && <Check className="w-3 h-3 text-emerald-600" />}
                    </div>
                    <div className="text-xs font-medium text-slate-900">{idx + 1}. {s.label}</div>
                  </button>
                );
              })}
            </div>

            {/* Active step content */}
            <div className="flex-1 overflow-y-auto p-5">
              {step === 'precheck'    && <StepPrecheck    closure={closure} refresh={refresh} onClose={onClose} goStep={goStep} canEdit={canEdit} />}
              {step === 'bookings'    && <StepBookings    closure={closure} refresh={refresh} markDone={markStepDone} onClose={onClose} />}
              {step === 'attachments' && <StepAttachments closure={closure} refresh={refresh}
                selected={attachmentSelected} setSelected={setAttachmentSelected} />}
              {step === 'report'      && <StepReport      closure={closure} refresh={refresh} markDone={markStepDone} />}
              {step === 'finalize'    && <StepFinalize    closure={closure} refresh={refresh} onFinalized={onFinalized} onClose={onClose} />}
            </div>

            {/* Footer nav */}
            <div className="border-t border-zinc-200 px-5 py-3 bg-zinc-50 flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => {
                const idx = STEPS.findIndex(s => s.id === step);
                if (idx > 0) goStep(STEPS[idx - 1].id);
              }} disabled={step === STEPS[0].id} data-testid="wizard-prev-btn">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" />Zurück
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={async () => {
                  const skipped = Array.from(new Set([...(closure.skipped_steps || []), step]));
                  await axios.put(`${API}/advisor/closures/${closure.id}`,
                    { skipped_steps: skipped }, { headers: H() });
                  setClosure(c => ({ ...c, skipped_steps: skipped }));
                  const idx = STEPS.findIndex(s => s.id === step);
                  if (idx < STEPS.length - 1) goStep(STEPS[idx + 1].id);
                }} data-testid="wizard-skip-btn">
                  Überspringen
                </Button>
                {(() => {
                  const cfg = stepActions[step];
                  const disabled = actionRunning
                    || (cfg?.disabledIf && cfg.disabledIf());
                  return (
                    <Button size="sm" onClick={handleStepAction} disabled={disabled}
                      className={cfg?.isFinal
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'}
                      data-testid="wizard-next-btn">
                      {actionRunning && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                      {cfg?.label || 'Weiter'}
                      {!cfg?.isFinal && <ChevronRight className="w-3.5 h-3.5 ml-1" />}
                    </Button>
                  );
                })()}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Help-Overlay — aktiviert via `?` oder Help-Button im Header */}
      <HelpOverlay
        isOpen={help.isOpen}
        onClose={help.close}
        title="Wizard — Tastatur-Shortcuts"
        sections={[
          {
            title: 'Navigation',
            shortcuts: [
              { keys: ['?'], label: 'Diese Hilfe öffnen / schließen' },
              { keys: ['Esc'], label: 'Wizard schließen' },
              { keys: ['1', '…', '5'], label: 'Direkt zu Schritt 1–5' },
              { keys: ['←'], label: 'Vorheriger Schritt' },
              { keys: ['→'], label: 'Nächster Schritt' },
            ],
          },
          {
            title: 'Aktionen (Footer)',
            shortcuts: [
              { keys: ['Klick', 'Zurück'], label: 'Zum vorherigen Schritt' },
              { keys: ['Klick', 'Überspringen'], label: 'Schritt überspringen' },
              { keys: ['Klick', 'Weiter'], label: 'Schritt-Aktion ausführen (z.B. PDF erzeugen, festschreiben)' },
            ],
          },
          {
            title: 'Schritt-Übersicht',
            shortcuts: [
              { keys: ['1'], label: 'Pre-Check (Plausi-Findings)' },
              { keys: ['2'], label: 'Buchungen prüfen' },
              { keys: ['3'], label: 'Anlagen anhängen' },
              { keys: ['4'], label: 'PDF · DATEV · ZIP erzeugen' },
              { keys: ['5'], label: 'Festschreiben + UStVA-Entwurf' },
            ],
          },
        ]}
      />
    </div>
  );
}

function periodLabel(rev) {
  if (!rev) return '—';
  const months = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
  if (rev.period_type === 'year') return `${rev.year}`;
  if (rev.period_type === 'quarter') return `Q${rev.period_value} ${rev.year}`;
  if (rev.period_type === 'month') return `${months[(rev.period_value || 1) - 1]} ${rev.year}`;
  return `${rev.year}`;
}

// ───────────────────── Step 1 — Precheck ─────────────────────
function StepPrecheck({ closure, refresh, onClose, goStep, canEdit = false }) {
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'dismiss'|'query', finding }
  const [modalText, setModalText] = useState('');
  const findings = closure.precheck_findings || [];
  const dismissed = closure.precheck_dismissed || [];
  const periodLabel = closure.period_label
    || (closure.period_type === 'year' ? `${closure.year}`
      : closure.period_type === 'quarter' ? `Q${closure.period_value} ${closure.year}`
      : `${closure.year}`);

  const run = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/advisor/closures/${closure.id}/precheck`, {}, { headers: H() });
      await refresh();
      toast.success('Pre-Check ausgeführt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setRunning(false);
    }
  };

  // ── Befund-Aktionen (gespiegelt aus der Belegprüfung) ──────────────────
  const keyPayload = (f) => (
    f.code === 'missing_recurring'
      ? { code: f.code, vendor_key: f.hint?.vendor_key, missing_period: f.hint?.missing_period }
      : { code: f.code, entity_type: f.entity_type, entity_id: f.entity_id }
  );
  const openDismiss = (f) => { setModal({ mode: 'dismiss', finding: f }); setModalText(''); };
  const openQuery = (f) => {
    setModal({ mode: 'query', finding: f });
    setModalText(`Rückfrage zur Prüfung: ${f.message}\n\nBitte um Klärung / fehlenden Beleg.`);
  };
  const closeModal = () => { setModal(null); setModalText(''); };

  const afterChange = async () => { await run(); };

  const submitDismiss = async () => {
    if (!modalText.trim()) { toast.error('Bitte eine Begründung angeben.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${closure.client_id}/findings/dismiss`,
        { ...keyPayload(modal.finding), reason: modalText.trim() }, { headers: H() });
      toast.success('Befund abgelehnt');
      closeModal(); setShowDismissed(true); await afterChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ablehnen fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  const submitQuery = async () => {
    if (!modalText.trim()) { toast.error('Bitte eine Nachricht eingeben.'); return; }
    setBusy(true);
    try {
      const f = modal.finding;
      const linkable = ['expense', 'invoice'].includes(f.entity_type) && f.entity_id;
      await axios.post(`${API}/advisor/clients/${closure.client_id}/comments`, {
        entity_type: linkable ? f.entity_type : (f.entity_type || 'note'),
        entity_id: linkable ? f.entity_id : null,
        text: modalText.trim(),
      }, { headers: H() });
      toast.success('Rückfrage an den Mandanten gesendet');
      closeModal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Senden fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  const restore = async (f) => {
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${closure.client_id}/findings/restore`, keyPayload(f), { headers: H() });
      toast.success('Befund wiederhergestellt'); await afterChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  const markDuplicate = async (f) => {
    if (!f.entity_id) return;
    if (!window.confirm('Diese Ausgabe als Duplikat bestätigen und die Doppelbuchung entfernen?\n\nDer Eintrag wird gelöscht (mit Audit-Eintrag). Dies kann nicht rückgängig gemacht werden.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${closure.client_id}/expense/${f.entity_id}/mark-duplicate`,
        { reason: 'Als Duplikat bestätigt (Abschluss-Pre-Check)' }, { headers: H() });
      toast.success('Duplikat entfernt'); await afterChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const confirmZeroVat = async (f) => {
    if (!f.entity_id) return;
    if (!window.confirm('Diese Ausgabe als 0 % (steuerfrei) bestätigen?\n\nDer MwSt-Satz wird auf 0 % gesetzt (mit Audit-Eintrag).')) return;
    setBusy(true);
    try {
      await axios.put(`${API}/advisor/clients/${closure.client_id}/expense/${f.entity_id}`,
        { vat_rate: 0 }, { headers: H() });
      toast.success('Als 0 % (steuerfrei) bestätigt'); await afterChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  // Group findings by code for easier scanning
  const grouped = {};
  for (const f of findings) {
    const k = f.code || 'other';
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(f);
  }
  const codeMeta = {
    missing_receipt:    { label: 'Belege fehlen' },
    stale_sent_invoice: { label: 'Versendet aber überfällig' },
    missing_paid_at:    { label: 'Bezahldatum fehlt' },
    open_inbox_thread:  { label: 'Offene Rückfragen' },
    missing_vat_amount: { label: 'USt-Betrag fehlt' },
    missing_vat_rate:   { label: 'MwSt-Satz nicht erfasst' },
    vat_mismatch:       { label: 'USt-Plausibilität' },
    possible_duplicate: { label: 'Mögliche Duplikate' },
    items_unreviewed:   { label: 'Noch nicht geprüft' },
    missing_recurring:  { label: 'Vermutet fehlende wiederkehrende Rechnungen' },
  };

  // Helper: deep-link into the records list — öffnet den Eintrag in einem NEUEN Tab,
  // damit der Abschluss-Wizard erhalten bleibt.
  const deepLinkToItem = (entityType, entityId) => {
    if (!entityType || !entityId) return;
    if (entityType === 'invoice' || entityType === 'expense') {
      window.open(`/advisor/client/${closure.client_id}?focus=${entityType}:${entityId}&year=${closure.year}`, '_blank', 'noopener');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-slate-900">Automatische Prüfung</h3>
          <Button size="sm" onClick={run} disabled={running}
            className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="run-precheck-btn">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileSearch className="w-3.5 h-3.5 mr-1" />}
            {closure.precheck_run_at ? 'Erneut prüfen' : 'Prüfung starten'}
          </Button>
        </div>
        {closure.precheck_run_at && (
          <div className="text-xs text-slate-500">
            Letzter Lauf: {new Date(closure.precheck_run_at).toLocaleString('de-DE')} · {findings.length} Auffälligkeit{findings.length === 1 ? '' : 'en'}
          </div>
        )}
      </div>

      {!closure.precheck_run_at && findings.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-6 text-center text-sm text-slate-500">
          Klicke „Prüfung starten", um Belege, Bezahldaten, USt-Plausibilität, Duplikate und offene Vorgänge zu prüfen.
        </div>
      ) : findings.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3" data-testid="precheck-clear">
          <Check className="w-5 h-5 text-emerald-600" />
          <div className="text-sm text-emerald-800 font-medium">Keine offenen Auffälligkeiten.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="precheck-findings">
          {Object.entries(grouped).map(([code, items]) => (
            <div key={code} className="border border-zinc-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                <span>{codeMeta[code]?.label || code}</span>
                <span className="text-slate-400 normal-case font-normal">{items.length}</span>
              </div>
              <div className="divide-y divide-zinc-100">
                {items.map((f, i) => {
                  const Icon = SEVERITY_ICON[f.severity] || Info;
                  const linkable = (f.entity_type === 'invoice' || f.entity_type === 'expense') && f.entity_id;
                  const isIfb = f.code === 'ifb_unused';
                  const isDup = f.code === 'possible_duplicate';
                  return (
                    <div key={i} className={`px-3 py-2 flex items-start gap-2.5 text-sm ${SEVERITY_CSS[f.severity] || SEVERITY_CSS.info}`}
                      data-testid={`precheck-finding-${f.code}-${i}`}>
                      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">{f.message}</div>
                      {isIfb && goStep && (
                        <button onClick={() => {
                          goStep('bookings');
                          setTimeout(() => {
                            const el = document.querySelector('[data-testid="advisor-ifb-control"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 400);
                        }}
                          className="text-xs px-2 py-0.5 bg-white border border-current rounded hover:bg-zinc-50 inline-flex items-center gap-1 shrink-0"
                          data-testid={`finding-ifb-link-${i}`}
                          title="Investitionsfreibetrag festlegen">
                          Jetzt festlegen <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      <span className="flex items-center gap-0.5 shrink-0">
                        {linkable && (
                          <button onClick={() => deepLinkToItem(f.entity_type, f.entity_id)}
                            className="p-1 rounded hover:bg-white/70" data-testid={`finding-deeplink-${i}`}
                            title="In neuem Tab öffnen">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {isDup && canEdit && (
                          <button onClick={() => markDuplicate(f)} disabled={busy}
                            className="p-1 rounded hover:bg-white/70 hover:text-rose-700 disabled:opacity-50"
                            data-testid={`finding-mark-duplicate-${i}`} title="Als Duplikat bestätigen & entfernen">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {f.code === 'missing_vat_rate' && canEdit && f.entity_id && (
                          <button onClick={() => confirmZeroVat(f)} disabled={busy}
                            className="p-1 rounded hover:bg-white/70 hover:text-emerald-700 disabled:opacity-50"
                            data-testid={`finding-confirm-zero-vat-${i}`} title="Als 0 % (steuerfrei) bestätigen">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => openQuery(f)} disabled={busy}
                          className="p-1 rounded hover:bg-white/70 hover:text-blue-700 disabled:opacity-50"
                          data-testid={`finding-query-${f.code}-${i}`} title="Rückfrage an den Mandanten">
                          <Send className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => openDismiss(f)} disabled={busy}
                          className="p-1 rounded hover:bg-white/70 hover:text-amber-700 disabled:opacity-50"
                          data-testid={`finding-dismiss-${f.code}-${i}`} title="Befund ablehnen (mit Begründung)">
                          <Ban className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Abgelehnte Befunde (GoBD-dokumentiert) */}
      {dismissed.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2" data-testid="precheck-dismissed-section">
          <button onClick={() => setShowDismissed((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="toggle-dismissed-precheck">
            <Ban className="w-3 h-3" />
            {showDismissed ? 'Abgelehnte Befunde ausblenden' : `${dismissed.length} abgelehnte(r) Befund(e) anzeigen`}
          </button>
          {showDismissed && (
            <ul className="mt-2 space-y-1.5">
              {dismissed.map((f, i) => (
                <li key={`dism-${i}`} className="flex items-start justify-between gap-3 text-[11px] text-slate-500"
                  data-testid={`dismissed-precheck-${f.code}-${i}`}>
                  <span className="min-w-0">
                    <span className="line-through decoration-slate-300">{f.message}</span>
                    {f.dismiss_reason && <span className="text-slate-400"> — Begründung: „{f.dismiss_reason}"</span>}
                    {f.dismissed_by && <span className="text-slate-400"> ({f.dismissed_by})</span>}
                  </span>
                  <button onClick={() => restore(f)} disabled={busy}
                    className="shrink-0 inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 disabled:opacity-50"
                    data-testid={`restore-precheck-${f.code}-${i}`} title="Wiederherstellen">
                    <Undo2 className="w-3 h-3" />Wiederherstellen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Modal: Ablehnen mit Begründung / Rückfrage an Mandant */}
      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="precheck-action-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-slate-900">
                {modal.mode === 'dismiss' ? 'Befund ablehnen' : 'Rückfrage an den Mandanten'}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-700" data-testid="precheck-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">{modal.finding?.message}</p>
            <textarea value={modalText} onChange={(e) => setModalText(e.target.value)}
              rows={modal.mode === 'dismiss' ? 3 : 5} autoFocus
              placeholder={modal.mode === 'dismiss' ? 'Begründung (z. B. kein Duplikat, Beleg liegt vor, einmalig) …' : 'Nachricht an den Mandanten …'}
              className="w-full text-sm border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="precheck-modal-textarea" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={closeModal} disabled={busy}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="precheck-modal-cancel">Abbrechen</button>
              <button onClick={modal.mode === 'dismiss' ? submitDismiss : submitQuery} disabled={busy || !modalText.trim()}
                className={`px-3 py-1.5 text-sm rounded-lg text-white disabled:opacity-50 ${modal.mode === 'dismiss' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                data-testid="precheck-modal-confirm">
                {busy ? '…' : (modal.mode === 'dismiss' ? 'Ablehnen' : 'Senden')}
              </button>
            </div>
          </div>
        </div>
      )}

      {closure.precheck_run_at && (
        <CommentSection closure={closure} step="precheck" refresh={refresh} />
      )}
    </div>
  );
}

// ───────────────────── Step 2 — Bookings (Übersicht: geprüft / offen / Hinweise) ─────────────────────
function StepBookings({ closure, refresh, markDone, onClose }) {
  const [items, setItems] = useState({ invoices: [], expenses: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unreviewed | flagged

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invR, expR] = await Promise.all([
        axios.get(`${API}/advisor/clients/${closure.client_id}/data?section=invoices&year=${closure.year}`, { headers: H() }),
        axios.get(`${API}/advisor/clients/${closure.client_id}/data?section=expenses&year=${closure.year}`, { headers: H() }),
      ]);
      const invs = (invR.data?.data?.invoices || []).filter(i => inPeriod(i.paid_at || i.invoice_date, closure));
      const exps = (expR.data?.data?.expenses || []).filter(e => inPeriod(e.paid_at || e.date, closure));
      setItems({ invoices: invs, expenses: exps });
    } finally {
      setLoading(false);
    }
  }, [closure]);

  useEffect(() => { load(); }, [load]);

  // Build a map: entity_id → list of pre-check findings (so we can show inline hints)
  const findingsByItem = (() => {
    const map = {};
    for (const f of closure.precheck_findings || []) {
      if (!f.entity_id || (f.entity_type !== 'invoice' && f.entity_type !== 'expense')) continue;
      if (!map[f.entity_id]) map[f.entity_id] = [];
      map[f.entity_id].push(f);
    }
    return map;
  })();

  const itemStatus = (it) => {
    if (it.is_locked) return { code: 'locked', label: 'Gesperrt', cls: 'bg-zinc-100 text-zinc-500 border-zinc-300', dot: 'bg-zinc-400', priority: 99 };
    const findings = findingsByItem[it.id] || [];
    const hasError = findings.some(f => f.severity === 'error');
    const hasWarn = findings.some(f => f.severity === 'warn');
    const hasOpenThread = (it.open_thread_count || 0) > 0;
    if (hasError) return { code: 'error', label: 'Fehler', cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', priority: 1 };
    if (hasOpenThread) return { code: 'flagged', label: 'Rückfrage', cls: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', priority: 2 };
    if (hasWarn) return { code: 'warn', label: 'Hinweis', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', priority: 3 };
    if (it.reviewed_by_advisor) return { code: 'reviewed', label: 'Geprüft', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', priority: 5 };
    return { code: 'pending', label: 'Offen', cls: 'bg-zinc-50 text-zinc-500 border-zinc-200', dot: 'bg-zinc-300', priority: 4 };
  };

  const allItems = [
    ...items.invoices.map(i => ({ ...i, _type: 'invoice', _date: i.paid_at || i.invoice_date, _label: i.invoice_number, _sub: i.customer_name, _amount: i.total })),
    ...items.expenses.map(e => ({ ...e, _type: 'expense', _date: e.paid_at || e.date, _label: e.description || e.vendor || '—', _sub: catLabelDe(e.category), _amount: -(e.amount || 0) })),
  ];

  const enriched = allItems.map(it => ({ ...it, _status: itemStatus(it), _findings: findingsByItem[it.id] || [] }))
    .sort((a, b) => a._status.priority - b._status.priority || (a._date || '').localeCompare(b._date || ''));

  const filtered = enriched.filter(it => {
    if (filter === 'unreviewed') return it._status.code !== 'reviewed' && it._status.code !== 'locked';
    if (filter === 'flagged') return ['error', 'flagged', 'warn'].includes(it._status.code);
    return true;
  });

  const counters = {
    total: enriched.length,
    reviewed: enriched.filter(i => i._status.code === 'reviewed').length,
    flagged: enriched.filter(i => ['error', 'flagged', 'warn'].includes(i._status.code)).length,
    pending: enriched.filter(i => i._status.code === 'pending').length,
  };

  // C9-Cleanup (Feb 2026): voller Item-Payload — hier aber: öffnen in NEUEM Tab,
  // damit der Abschluss-Wizard erhalten bleibt. Änderungen im neuen Tab triggern
  // via BroadcastChannel einen Auto-Refresh dieses Wizards.
  const openInDrawer = (it) => {
    window.open(`/advisor/client/${closure.client_id}?focus=${it._type}:${it.id}&year=${closure.year}`, '_blank', 'noopener');
  };

  if (loading) return <div className="text-sm text-slate-500 flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2" />Laden...</div>;

  return (
    <div className="space-y-4" data-testid="step-bookings">
      {/* Counters Header */}
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-2">Buchungen — Prüfungs-Übersicht</h3>
        <div className="grid grid-cols-4 gap-2 mb-3">
          <CounterTile label="Gesamt" value={counters.total} cls="bg-zinc-50 border-zinc-200 text-slate-700" />
          <CounterTile label="Geprüft" value={counters.reviewed} cls="bg-emerald-50 border-emerald-200 text-emerald-700" />
          <CounterTile label="Hinweise/Fehler" value={counters.flagged} cls="bg-amber-50 border-amber-200 text-amber-700" />
          <CounterTile label="Offen" value={counters.pending} cls="bg-zinc-50 border-zinc-200 text-slate-500" />
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Filter className="w-3 h-3" />Filter:
          {[
            { v: 'all', l: 'Alle' },
            { v: 'unreviewed', l: 'Nur ungeprüft' },
            { v: 'flagged', l: 'Mit Auffälligkeiten' },
          ].map(({ v, l }) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`px-2 py-0.5 rounded border ${filter === v ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-zinc-200 text-slate-600 hover:bg-zinc-50'}`}
              data-testid={`bookings-filter-${v}`}>
              {l}
            </button>
          ))}
          <span className="text-slate-400 ml-auto">{filtered.length} sichtbar</span>
        </div>
      </div>

      {/* Steuer-Optimierung (AT) — Investitionsfreibetrag, self-gating */}
      <AdvisorInvestmentAllowanceControl clientId={closure.client_id} year={closure.year} embedded />


      {filtered.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-6 text-center text-sm text-slate-500">
          {counters.total === 0 ? 'Keine Buchungen in dieser Periode.' : 'Keine Einträge im aktuellen Filter.'}
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-zinc-50 text-xs text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-24">Status</th>
                <th className="text-left px-3 py-2 font-medium w-24">Datum</th>
                <th className="text-left px-3 py-2 font-medium">Eintrag</th>
                <th className="text-left px-3 py-2 font-medium">Hinweise</th>
                <th className="text-right px-3 py-2 font-medium w-28">Brutto</th>
                <th className="px-2 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => (
                <tr key={`${it._type}-${it.id}`} className="border-t border-zinc-100 hover:bg-zinc-50/50"
                  data-testid={`booking-row-${it.id}`}>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border rounded ${it._status.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${it._status.dot}`} />
                      {it._status.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">
                    {(it._date || '').slice(0, 10) || '—'}
                  </td>
                  <td className="px-3 py-2 max-w-[260px]">
                    <div className="text-slate-900 truncate flex items-center gap-1.5">
                      {it._type === 'invoice' ? <FileText className="w-3 h-3 text-emerald-600 shrink-0" /> : <Archive className="w-3 h-3 text-rose-600 shrink-0" />}
                      <span className="truncate">{it._label}</span>
                      {it.is_locked && <Lock className="w-3 h-3 text-slate-400" />}
                    </div>
                    {it._sub && <div className="text-[11px] text-slate-500 truncate">{it._sub}</div>}
                  </td>
                  <td className="px-3 py-2 max-w-[280px]">
                    {it._findings.length > 0 ? (
                      <div className="space-y-0.5">
                        {it._findings.slice(0, 2).map((f, idx) => {
                          const Icon = SEVERITY_ICON[f.severity] || Info;
                          return (
                            <div key={idx} className={`text-[11px] flex items-start gap-1 ${
                              f.severity === 'error' ? 'text-red-700' : f.severity === 'warn' ? 'text-amber-700' : 'text-slate-600'
                            }`}>
                              <Icon className="w-3 h-3 mt-0.5 shrink-0" />
                              <span className="line-clamp-2">{f.message.replace(/^[^:]+:\s*/, '')}</span>
                            </div>
                          );
                        })}
                        {it._findings.length > 2 && (
                          <div className="text-[10px] text-slate-400">+{it._findings.length - 2} weitere</div>
                        )}
                      </div>
                    ) : (it.open_thread_count || 0) > 0 ? (
                      <div className="text-[11px] text-orange-700 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />{it.open_thread_count} offene Rückfrage{it.open_thread_count === 1 ? '' : 'n'}
                      </div>
                    ) : (
                      <span className="text-[11px] text-slate-300">—</span>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                    (it._amount || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'
                  }`}>
                    {(it._amount || 0) >= 0 ? '+' : '−'}{fmtEur(Math.abs(it._amount || 0)).replace('€', '').trim()} €
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => openInDrawer(it)}
                      className="text-xs px-2 py-1 border border-zinc-200 rounded text-slate-600 hover:bg-zinc-100 hover:text-slate-900 inline-flex items-center gap-1 whitespace-nowrap"
                      data-testid={`booking-open-${it.id}`}
                      title="Eintrag in neuem Tab öffnen">
                      <ExternalLink className="w-3 h-3" />Öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit-Log preview of corrections in this closure */}
      {(closure.corrections || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1">
            <Edit3 className="w-3.5 h-3.5 text-amber-600" />
            Bisherige Korrekturen ({closure.corrections.length})
          </h3>
          <div className="bg-amber-50 border border-amber-200 rounded-lg divide-y divide-amber-200" data-testid="corrections-log">
            {closure.corrections.map(c => (
              <div key={c.id} className="px-3 py-2 text-xs">
                <div className="font-mono text-amber-900">
                  {c.entity_type} · {c.field} · {String(c.old_value || '—').slice(0,18)} → {String(c.new_value || '—').slice(0,18)}
                </div>
                <div className="text-amber-800 mt-0.5">{c.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CommentSection closure={closure} step="bookings" refresh={refresh} />
    </div>
  );
}

function CounterTile({ label, value, cls }) {
  return (
    <div className={`border rounded-md px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

const _CAT_DE = {
  office: 'Büromaterial', software: 'Software', travel: 'Reise', hospitality: 'Bewirtung',
  vehicle: 'Fahrzeug', telecom: 'Telekom', insurance: 'Versicherung', education: 'Fortbildung',
  marketing: 'Marketing', rent: 'Miete', consulting: 'Beratung', equipment: 'Ausstattung', other: 'Sonstiges',
};
function catLabelDe(k) { return _CAT_DE[k] || k || '—'; }

function inPeriod(dateStr, closure) {
  if (!dateStr) return false;
  const d = String(dateStr).slice(0, 10);
  const y = closure.year;
  if (closure.period_type === 'year') return d.startsWith(String(y));
  if (closure.period_type === 'quarter') {
    const q = closure.period_value;
    const startM = (q - 1) * 3 + 1;
    const endM = startM + 2;
    const start = `${y}-${String(startM).padStart(2,'0')}-01`;
    const last = new Date(y, endM, 0).getDate();
    const end = `${y}-${String(endM).padStart(2,'0')}-${String(last).padStart(2,'0')}`;
    return d >= start && d <= end;
  }
  if (closure.period_type === 'month') {
    const m = String(closure.period_value).padStart(2,'0');
    return d.startsWith(`${y}-${m}`);
  }
  return false;
}

// ───────────────────── Step 3 — Attachments ─────────────────────
function StepAttachments({ closure, refresh, selected, setSelected }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/tresor/documents?client_id=${closure.client_id}`, { headers: H() });
        const list = (r.data?.documents || []).filter(d => !d.is_deleted);
        setDocs(list);
        // Initial-State: übernimm bestehende closure.attachment_doc_ids,
        // oder wähle alle Tresor-Docs vor (C4-Cleanup Feb 2026), wenn noch keine
        // Auswahl gespeichert ist und der Abschluss in Arbeit ist.
        const existing = new Set(closure.attachment_doc_ids || []);
        if (existing.size > 0) {
          setSelected(existing);
        } else if (list.length > 0 && closure.state === 'in_progress') {
          setSelected(new Set(list.map(d => d.id)));
        } else {
          setSelected(new Set());
        }
      } finally { setLoading(false); }
    })();
  }, [closure.client_id, closure.attachment_doc_ids, closure.state, setSelected]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-1">Anlagen verknüpfen</h3>
        <p className="text-xs text-slate-500">Wähle Dokumente aus, die zu diesem Abschluss gehören (z. B. Kontoauszüge, USt-Voranmeldungen). Klick auf <em>„Anlagen speichern & abschließen"</em> unten speichert die Auswahl und geht zum nächsten Schritt.</p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Dokumente laden...</div>
      ) : docs.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-sm text-slate-500 text-center">
          Keine Dokumente verfügbar. Lade sie zuerst im Dokumente-Tab hoch.
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg max-h-96 overflow-y-auto bg-white" data-testid="attachment-picker">
          {docs.map(d => (
            <label key={d.id} className="flex items-center gap-3 px-3 py-2 border-b border-zinc-100 last:border-0 cursor-pointer hover:bg-zinc-50">
              <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)}
                className="rounded" data-testid={`attach-doc-${d.id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 truncate">{d.original_filename}</div>
                <div className="text-xs text-slate-500">{d.folder} · v{d.version} · {(((d.size_bytes ?? d.size) || 0)/1024).toFixed(1)} KB</div>
              </div>
            </label>
          ))}
        </div>
      )}

      <div className="text-xs text-slate-500">{selected.size} ausgewählt</div>

      <CommentSection closure={closure} step="attachments" refresh={refresh} />
    </div>
  );
}

// ───────────────────── Step 4 — Report ─────────────────────
function StepReport({ closure, refresh, markDone }) {
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(null); // 'datev' | 'zip' | 'both' | null
  const generated = !!closure.report_pdf_doc_id;
  const kpis = closure.kpis || {};

  // Period date range derived from closure (consumed by /export endpoint)
  const periodRange = (() => {
    const y = closure.year;
    const pt = closure.period_type;
    const pv = closure.period_value;
    if (pt === 'year') return { from: `${y}-01-01`, to: `${y}-12-31` };
    if (pt === 'quarter' && pv) {
      const sm = (pv - 1) * 3 + 1;
      const em = sm + 2;
      const lastDay = new Date(y, em, 0).getDate();
      return {
        from: `${y}-${String(sm).padStart(2, '0')}-01`,
        to: `${y}-${String(em).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
      };
    }
    if (pt === 'month' && pv) {
      const lastDay = new Date(y, pv, 0).getDate();
      return {
        from: `${y}-${String(pv).padStart(2, '0')}-01`,
        to: `${y}-${String(pv).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
      };
    }
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  })();

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/advisor/closures/${closure.id}/generate-report`,
        {}, { headers: H() });
      toast.success(`Bericht erzeugt — ${r.data.filename}`);
      await refresh();
      await markDone('report');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erzeugen');
    } finally {
      setGenerating(false);
    }
  };

  // Reine Download-Logik; keine Toast/State-Effects, damit `exportBoth`
  // beides parallel auslösen kann ohne State-Race.
  const performExport = async (format) => {
    const includeReceipts = format === 'zip';
    const res = await axios.post(`${API}/advisor/clients/${closure.client_id}/export`, {
      format,
      date_from: periodRange.from,
      date_to: periodRange.to,
      include_invoices: true,
      include_expenses: true,
      include_receipts: includeReceipts,
    }, { headers: H(), responseType: 'blob' });
    const cd = res.headers['content-disposition'] || '';
    const m = cd.match(/filename="?(.+?)"?$/);
    const fname = m ? m[1] : `${periodLabelShort(closure)}_${format}.${format === 'datev' ? 'csv' : format}`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([res.data]));
    a.download = fname;
    a.click();
    return fname;
  };

  const exportBundle = async (format) => {
    setExporting(format);
    try {
      await performExport(format);
      toast.success(format === 'datev' ? 'DATEV-CSV heruntergeladen' : 'ZIP-Bundle heruntergeladen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExporting(null);
    }
  };

  // P1c-Improvement (Feb 2026) — Combo: SB nutzt DATEV + ZIP fast immer
  // im Tandem (DATEV in die SB-Software, ZIP als Mandanten-Akte).
  // Promise.allSettled, damit ein Fehler den anderen Download nicht abbricht.
  const exportBoth = async () => {
    setExporting('both');
    try {
      const results = await Promise.allSettled([performExport('datev'), performExport('zip')]);
      const datevOk = results[0].status === 'fulfilled';
      const zipOk = results[1].status === 'fulfilled';
      if (datevOk && zipOk) {
        toast.success('DATEV-CSV und ZIP-Bundle heruntergeladen');
      } else if (datevOk && !zipOk) {
        toast.error('DATEV-CSV OK, ZIP-Bundle fehlgeschlagen: ' + (results[1].reason?.response?.data?.detail || 'Fehler'));
      } else if (!datevOk && zipOk) {
        toast.error('ZIP-Bundle OK, DATEV-CSV fehlgeschlagen: ' + (results[0].reason?.response?.data?.detail || 'Fehler'));
      } else {
        toast.error('Beide Exporte fehlgeschlagen');
      }
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-1">Abschlussbericht & Exporte</h3>
        <p className="text-xs text-slate-500">
          Drei Lieferpakete für deine Akten und die Steuerberater-Software:
          PDF-Bericht (GoBD-tauglich), DATEV-Buchungsstapel (EXTF), ZIP-Bundle mit allen Belegen.
        </p>
      </div>

      {generated && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3" data-testid="report-summary">
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <Check className="w-4 h-4" />
            <span className="font-medium">Bericht erzeugt</span>
            <span className="text-xs text-emerald-700">
              {closure.report_generated_at && new Date(closure.report_generated_at).toLocaleString('de-DE')}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-600">Einnahmen netto</span><span className="font-mono font-medium">{fmtEur(kpis.income_netto)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Ausgaben netto</span><span className="font-mono font-medium">{fmtEur(kpis.expense_netto)}</span></div>
            <div className="flex justify-between border-t border-emerald-300 pt-1.5 mt-1 col-span-2"><span className="font-semibold">Gewinn (netto)</span><span className="font-mono font-semibold">{fmtEur(kpis.profit_netto)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">USt-Saldo</span><span className="font-mono">{fmtEur(kpis.vat_balance)}</span></div>
          </div>
        </div>
      )}

      {/* Three export tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ExportTile
          icon={FileText}
          accent="border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50"
          iconCls="text-emerald-700"
          title="PDF-Bericht"
          desc="GoBD-konformer Periodenabschluss mit KPIs, Korrekturen und Anlagen-Verweisen."
          loading={generating}
          done={generated}
          doneLabel="Erstellt"
          actionLabel={generated ? 'Neu erzeugen' : 'Erzeugen'}
          onClick={generate}
          testId="generate-report-btn"
        />
        <ExportTile
          icon={Download}
          accent="border-blue-300 bg-blue-50/40 hover:bg-blue-50"
          iconCls="text-blue-700"
          title="DATEV-CSV"
          desc="EXTF-Buchungsstapel im DATEV-Format — direkt in DATEV Rechnungswesen importierbar."
          loading={exporting === 'datev' || exporting === 'both'}
          disabled={exporting === 'both'}
          actionLabel="Exportieren"
          onClick={() => exportBundle('datev')}
          testId="export-datev-btn"
        />
        <ExportTile
          icon={Archive}
          accent="border-amber-300 bg-amber-50/40 hover:bg-amber-50"
          iconCls="text-amber-700"
          title="ZIP-Bundle"
          desc="Alles in einem Paket: Buchungs-CSVs, Rechnungs-PDFs und Belege als Originaldateien."
          loading={exporting === 'zip' || exporting === 'both'}
          disabled={exporting === 'both'}
          actionLabel="Bundle herunterladen"
          onClick={() => exportBundle('zip')}
          testId="export-zip-btn"
        />
      </div>

      {/* P1c-Improvement (Feb 2026): Combo-Action für DATEV + ZIP, da
       * SBs beide fast immer gemeinsam an die Software/Akte übergeben. */}
      <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        data-testid="export-combo-row">
        <div className="flex items-start gap-2.5">
          <div className="bg-white border border-slate-200 rounded-md p-1.5">
            <Download className="w-4 h-4 text-slate-700" strokeWidth={1.7} />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">DATEV + ZIP gleichzeitig</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Spart 1 Klick pro Quartal — DATEV-CSV in die Software, ZIP-Bundle für die Mandanten-Akte.
            </div>
          </div>
        </div>
        <Button size="sm" onClick={exportBoth} disabled={exporting !== null}
          className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
          data-testid="export-combo-btn">
          {exporting === 'both' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
          {exporting === 'both' ? 'Beide werden geladen…' : 'Beide herunterladen'}
        </Button>
      </div>

      <div className="text-[11px] text-slate-500 italic flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        Zeitraum: {periodRange.from} bis {periodRange.to}. Der DATEV-Export ist nur für DE-Mandanten relevant.
      </div>

      <CommentSection closure={closure} step="report" refresh={refresh} />
    </div>
  );
}

function ExportTile({ icon: Icon, accent, iconCls, title, desc, loading, disabled, done, doneLabel, actionLabel, onClick, testId }) {
  return (
    <div className={`border rounded-lg p-3 transition-colors ${accent}`}>
      <div className="flex items-start justify-between mb-2">
        <Icon className={`w-5 h-5 ${iconCls}`} strokeWidth={1.7} />
        {done && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white font-medium uppercase tracking-wider">
            {doneLabel || 'OK'}
          </span>
        )}
      </div>
      <div className="text-sm font-semibold text-slate-900 mb-1">{title}</div>
      <div className="text-[11px] text-slate-600 mb-3 leading-relaxed">{desc}</div>
      <Button size="sm" onClick={onClick} disabled={loading || disabled}
        className="bg-slate-900 hover:bg-slate-800 text-white w-full text-xs h-8 rounded-md"
        data-testid={testId}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
        {actionLabel}
      </Button>
    </div>
  );
}

function periodLabelShort(closure) {
  const y = closure.year;
  if (closure.period_type === 'year') return `${y}`;
  if (closure.period_type === 'quarter') return `Q${closure.period_value}_${y}`;
  if (closure.period_type === 'month') return `${String(closure.period_value).padStart(2, '0')}_${y}`;
  return `${y}`;
}

// ───────────────────── Step 5 — Finalize ─────────────────────
// vaPeriod-Mapping zentral, damit StepFinalize + VoranmeldungDraftCTA
// dieselben Werte nutzen.
function deriveVaPeriod(closure) {
  const pt = closure.period_type;
  const pv = closure.period_value;
  if (pt === 'year') return { period_type: 'yearly', year: closure.year, period_index: null };
  if (pt === 'quarter') return { period_type: 'quarterly', year: closure.year, period_index: pv };
  if (pt === 'month') return { period_type: 'monthly', year: closure.year, period_index: pv };
  return null;
}
function matchesVaPeriod(filing, vaPeriod) {
  if (filing.period_type !== vaPeriod.period_type) return false;
  if (vaPeriod.period_type === 'yearly') return true;
  const startMonth = parseInt((filing.period_start || '').slice(5, 7), 10);
  if (vaPeriod.period_type === 'monthly') return startMonth === vaPeriod.period_index;
  if (vaPeriod.period_type === 'quarterly') {
    const q = Math.floor((startMonth - 1) / 3) + 1;
    return q === vaPeriod.period_index;
  }
  return false;
}

function StepFinalize({ closure, refresh, onFinalized, onClose }) {
  const [finalizing, setFinalizing] = useState(false);
  const [existingFiling, setExistingFiling] = useState(null);
  const isCompleted = closure.state === 'completed';
  const vaPeriod = deriveVaPeriod(closure);
  const canFinalize = !!closure.report_pdf_doc_id && !isCompleted;

  // Existing Voranmeldung-Filing für genau diese Periode laden
  useEffect(() => {
    if (!vaPeriod) { setExistingFiling(null); return; }
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/advisor/clients/${closure.client_id}/voranmeldung?year=${vaPeriod.year}`,
          { headers: H() }
        );
        if (!alive) return;
        const found = (res.data.filings || []).find(f => matchesVaPeriod(f, vaPeriod));
        setExistingFiling(found || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [closure.client_id, closure.period_type, closure.period_value, closure.year]);

  const finalize = async ({ withVoranmeldung = false } = {}) => {
    const msg = withVoranmeldung
      ? 'Abschluss festschreiben & UStVA-Entwurf erstellen?\n\nDie Periode wird GoBD-konform gesperrt, der Bericht per E-Mail an den Mandanten versendet, und der USt-Voranmeldungs-Entwurf für diese Periode wird in einem Schritt gespeichert (im Voranmeldung-Tab weiter bearbeitbar).'
      : 'Abschluss jetzt festschreiben?\n\nDie Periode wird GoBD-konform gesperrt und der Bericht per E-Mail an den Mandanten versendet. Spätere Änderungen sind nur mit dokumentiertem Aufbrechen möglich.';
    if (!window.confirm(msg)) return;
    setFinalizing(true);
    try {
      const r = await axios.post(`${API}/advisor/closures/${closure.id}/finalize`,
        {}, { headers: H() });
      const emailHint = r.data.email_sent ? '& E-Mail versandt' : '(E-Mail-Versand fehlgeschlagen — bitte später erneut prüfen)';

      if (withVoranmeldung && vaPeriod) {
        try {
          const va = await axios.post(
            `${API}/advisor/clients/${closure.client_id}/voranmeldung`,
            vaPeriod, { headers: H() }
          );
          setExistingFiling(va.data.filing);
          toast.success(`Abschluss festgeschrieben ${emailHint} · UStVA-Entwurf gespeichert`);
        } catch (e) {
          toast.error(`Abschluss OK, UStVA-Entwurf fehlgeschlagen: ${e.response?.data?.detail || 'Fehler'}`);
        }
      } else {
        toast.success(`Abschluss festgeschrieben ${emailHint}`);
      }
      await refresh();
      if (onFinalized) onFinalized();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Festschreiben');
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-1">Festschreiben</h3>
        <p className="text-xs text-slate-500">Sperre die Periode (GoBD-Lock) und sende den Bericht per E-Mail an den Mandanten.</p>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          {closure.report_pdf_doc_id ? <Check className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-500" />}
          <span>Bericht erzeugt</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600" />
          <span>{(closure.attachment_doc_ids || []).length} Anlage(n) verknüpft</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600" />
          <span>{(closure.corrections || []).length} Korrektur(en) im Audit</span>
        </div>
      </div>

      {isCompleted ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="finalize-completed">
          <div className="flex items-center gap-2 text-sm text-emerald-800 font-medium mb-1">
            <Lock className="w-4 h-4" />Abschluss festgeschrieben
          </div>
          <div className="text-xs text-emerald-700">
            {closure.finalized_at && new Date(closure.finalized_at).toLocaleString('de-DE')}
            {closure.finalized_email_sent_at && ' · E-Mail versandt'}
          </div>
          <Button variant="outline" size="sm" onClick={onClose} className="mt-3">
            Schließen
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => finalize()} disabled={finalizing || !canFinalize}
              className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="finalize-btn">
              {finalizing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              <Send className="w-3.5 h-3.5 mr-1" />
              Festschreiben & senden
            </Button>
            {!existingFiling && vaPeriod && (
              <Button size="sm" onClick={() => finalize({ withVoranmeldung: true })} disabled={finalizing || !canFinalize}
                className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="finalize-with-va-btn">
                {finalizing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
                <Send className="w-3.5 h-3.5 mr-1" />
                Festschreiben + UStVA-Entwurf
              </Button>
            )}
          </div>
          {!existingFiling && vaPeriod && canFinalize && (
            <div className="text-[11px] text-slate-500 italic flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              1-Klick-Variante: Festschreiben + automatisch USt-Voranmeldungs-Entwurf für {periodLabelShort(closure)} erstellen.
            </div>
          )}
        </div>
      )}

      {/* P1c (Feb 2026) — Brücke zwischen Abschluss & USt-Voranmeldung.
       * existingFiling kommt jetzt aus dem Parent (StepFinalize), damit der
       * Combo-Button und die CTA immer denselben Stand sehen.
       */}
      <VoranmeldungDraftCTA closure={closure} onClose={onClose}
        vaPeriod={vaPeriod} existingFiling={existingFiling} setExistingFiling={setExistingFiling} />

      <CommentSection closure={closure} step="finalize" refresh={refresh} />
    </div>
  );
}

// ───────────── Voranmeldung-Brücke (P1c) ─────────────
// Bietet im Schritt „Festschreiben" eine direkte Erstellung der USt-Voranmeldung
// für genau diesen Zeitraum. Mappt closure.period_type → voranmeldung-Periode,
// nutzt die SB-Routen mit Vollmacht (mit Audit-Log).
//
// State-Sharing: vaPeriod + existingFiling kommen aus dem Parent (StepFinalize),
// damit der Combo-Button und die CTA immer denselben Stand sehen.
function VoranmeldungDraftCTA({ closure, onClose, vaPeriod, existingFiling, setExistingFiling }) {
  const [computation, setComputation] = useState(null);
  const [savedFiling, setSavedFiling] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingXml, setDownloadingXml] = useState(false);

  if (!vaPeriod) return null;

  const handleCompute = async () => {
    setComputing(true);
    try {
      const res = await axios.post(
        `${API}/advisor/clients/${closure.client_id}/voranmeldung/compute`,
        vaPeriod, { headers: H() }
      );
      setComputation(res.data.computation);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Berechnung fehlgeschlagen');
    } finally {
      setComputing(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/advisor/clients/${closure.client_id}/voranmeldung`,
        vaPeriod, { headers: H() }
      );
      setSavedFiling(res.data.filing);
      if (setExistingFiling) setExistingFiling(res.data.filing);
      toast.success(res.data.updated_existing ? 'Bestehender Entwurf aktualisiert' : 'USt-Voranmeldung als Entwurf gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadXml = async (filing) => {
    setDownloadingXml(true);
    try {
      const res = await axios.get(
        `${API}/advisor/clients/${closure.client_id}/voranmeldung/${filing.id}/xml`,
        { headers: H(), responseType: 'blob' }
      );
      const fname = `${filing.country}_voranmeldung_${(filing.period_label || '').replace(/\s/g, '_')}.xml`;
      downloadBlob(res, fname, 'application/xml');
      toast.success('XML heruntergeladen');
    } catch (e) {
      toast.error(await extractBlobError(e, 'Download fehlgeschlagen'));
    } finally {
      setDownloadingXml(false);
    }
  };

  const goToVoranmeldungTab = () => {
    window.dispatchEvent(new CustomEvent('ivoliq:advisor-switch-tab', { detail: { tab: 'voranmeldung' } }));
    if (onClose) onClose();
  };

  // Anzeige-Kontext
  const filing = savedFiling || existingFiling;
  const showCompute = !filing && !computation;
  const showResult = !!computation && !filing;
  const showSaved = !!filing;

  return (
    <div className="border-2 border-blue-200 bg-blue-50/30 rounded-lg p-4 space-y-3" data-testid="voranmeldung-cta">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="bg-blue-100 rounded-lg p-1.5">
            <ReceiptText className="w-4 h-4 text-blue-700" strokeWidth={1.7} />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">USt-Voranmeldung erzeugen</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Direkt aus dem Abschluss heraus — Engine zieht Buchungen dieser Periode und liefert ELSTER/FinanzOnline-XML.
            </div>
          </div>
        </div>
      </div>

      {showCompute && (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleCompute} disabled={computing}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="voranmeldung-compute-from-closure-btn">
            {computing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ReceiptText className="w-3.5 h-3.5 mr-1" />}
            {computing ? 'Berechne…' : 'Vorschau berechnen'}
          </Button>
          <span className="text-xs text-slate-500">Optional — kann auch später unter „Voranmeldung" erfolgen.</span>
        </div>
      )}

      {showResult && (
        <div className="space-y-3" data-testid="voranmeldung-cta-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <MiniKpi label="Umsätze netto" value={fmtEur(computation.revenue_net)} />
            <MiniKpi label="Umsatzsteuer" value={`+ ${fmtEur(computation.revenue_vat)}`} cls="text-rose-700" />
            <MiniKpi label="Vorsteuer" value={`− ${fmtEur(computation.input_vat)}`} cls="text-emerald-700" />
            <MiniKpi
              label={computation.payable_vat >= 0 ? 'Zahllast' : 'Erstattung'}
              value={fmtEur(Math.abs(computation.payable_vat))}
              cls={computation.payable_vat >= 0 ? 'text-rose-700 font-bold' : 'text-emerald-700 font-bold'}
              icon={computation.payable_vat >= 0 ? ArrowUp : ArrowDown}
            />
          </div>
          {computation.warnings && computation.warnings.length > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {computation.warnings.length} Plausi-Hinweis{computation.warnings.length === 1 ? '' : 'e'} —
              werden im Voranmeldung-Tab im Detail angezeigt.
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveDraft} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="voranmeldung-save-draft-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Als Entwurf speichern
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setComputation(null)} data-testid="voranmeldung-discard-btn">
              Verwerfen
            </Button>
          </div>
        </div>
      )}

      {showSaved && (
        <div className="space-y-2" data-testid="voranmeldung-cta-saved">
          <div className="bg-white border border-blue-200 rounded p-2.5 text-xs flex items-center justify-between">
            <div>
              <div className="font-medium text-slate-900">
                {filing.country === 'AT' ? '🇦🇹 UVA' : '🇩🇪 UStVA'} · {filing.period_label || vaPeriod.year}
              </div>
              <div className="text-slate-500 mt-0.5">
                Status: <span className="font-medium">{filing.status === 'draft' ? 'Entwurf' : filing.status === 'ready' ? 'Prüfungsfertig' : 'Eingereicht'}</span>
                {filing.computation?.payable_vat !== undefined && (
                  <> · Zahllast: <span className="font-mono font-semibold">{fmtEur(filing.computation.payable_vat)}</span></>
                )}
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => handleDownloadXml(filing)} disabled={downloadingXml}
                className="h-7 text-xs"
                data-testid="voranmeldung-download-xml-btn">
                {downloadingXml ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                XML
              </Button>
              <Button size="sm" onClick={goToVoranmeldungTab}
                className="h-7 text-xs bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="voranmeldung-goto-tab-btn">
                Zum Tab<ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-slate-500 italic">
            <Info className="w-3 h-3 inline mr-1" />
            XML ist {filing.country === 'AT' ? 'FinanzOnline' : 'ELSTER'}-konform formatiert (nicht ERiC-signiert).
            Im Voranmeldung-Tab kann später „Als eingereicht" markiert werden.
          </div>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ label, value, cls, icon: Icon }) {
  return (
    <div className="bg-white border border-blue-200 rounded px-2 py-1.5">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-mono tabular-nums ${cls || 'text-slate-900'}`}>
        {Icon && <Icon className="w-3 h-3 inline mr-0.5" />}
        {value}
      </div>
    </div>
  );
}

// ───────────────────── Comment box ─────────────────────
function CommentSection({ closure, step, refresh }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const stepComments = (closure.comments || []).filter(c => c.step === step);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/advisor/closures/${closure.id}/comment`,
        { text: text.trim(), step }, { headers: H() });
      setText('');
      await refresh();
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="border-t border-zinc-200 pt-3 mt-4">
      <div className="text-xs font-medium text-slate-700 flex items-center gap-1 mb-1">
        <MessageSquare className="w-3 h-3" />Anmerkungen zu diesem Schritt ({stepComments.length})
      </div>
      <div className="text-[10px] text-slate-400 mb-2 italic">
        Diese Anmerkungen landen automatisch im zentralen Posteingang des Mandanten — keine parallelen Kanäle.
      </div>
      {stepComments.length > 0 && (
        <div className="space-y-1 mb-2" data-testid={`comments-${step}`}>
          {stepComments.map(c => {
            const isFL = c.author_role === 'freelancer';
            return (
              <div key={c.id} className={`rounded p-2 text-xs border ${isFL ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className={`font-medium mb-0.5 flex items-center gap-1 ${isFL ? 'text-emerald-700' : 'text-blue-700'}`}>
                  {c.author_name || (isFL ? 'Mandant' : 'SB')} · {new Date(c.created_at).toLocaleString('de-DE')}
                </div>
                <div className={isFL ? 'text-emerald-900' : 'text-blue-900'}>{c.text}</div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2">
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
          placeholder="Anmerkung..."
          className="flex-1 text-xs border border-zinc-300 rounded px-2 py-1 resize-none focus:ring-1 focus:ring-slate-900"
          data-testid={`comment-input-${step}`} />
        <Button size="sm" onClick={submit} disabled={saving || !text.trim()}
          className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
          data-testid={`comment-submit-${step}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
        </Button>
      </div>
    </div>
  );
}
